import { type DgContent, dgVersionReasonText } from '@sigora/domain';

import type { GuideView } from '../api/client.ts';

/** One open edit: the question's wording and its options' wording, as typed so far. */
export interface GuideEdit {
  text: string;
  options: readonly { id: string; text: string }[];
}

/**
 * The step as a landed reword leaves it, before the read that confirms it: the question in the
 * researcher's words — its options' text matched by id, their number and order as they were —
 * marked as their wording, the guide at the version the reword wrote and unapproved, and that
 * version in the history with its reason.
 *
 * The pair a check would judge now names the new version, so the check held for the version
 * replaced says nothing about the guide on screen while the read is out.
 */
export const applyReword = (
  view: GuideView,
  questionId: string,
  edit: GuideEdit,
  version: number,
  now: Date = new Date(),
): GuideView => {
  if (view.dg === null) {
    return view;
  }

  const content: DgContent = {
    sections: view.dg.content.sections.map((section) => ({
      ...section,
      questions: section.questions.map((held) =>
        held.id !== questionId
          ? held
          : {
              ...held,
              text: edit.text,
              worded_by: 'user' as const,
              ...(held.options === undefined
                ? {}
                : {
                    options: held.options.map((option) => ({
                      ...option,
                      text:
                        edit.options.find((typed) => typed.id === option.id)?.text ?? option.text,
                    })),
                  }),
            },
      ),
    })),
  };

  return {
    ...view,
    dg: { ...view.dg, version, status: 'unapproved', content },
    // The version on screen is the researcher's rewording now, not a reconciled one.
    reconciliation: null,
    coverageInputs:
      view.coverageInputs === null ? null : { ...view.coverageInputs, dg_version: version },
    history: [
      ...view.history,
      {
        version,
        authoredBy: 'user',
        reason: 'reworded_by_you',
        reasonText: dgVersionReasonText({
          reason: 'reworded_by_you',
          previous: view.dg.content,
          current: content,
        }),
        createdAt: now.toISOString(),
      },
    ],
  };
};
